import React, { useContext, useEffect, useState } from "react"
import { Div, Text } from "atomize"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck, faChevronRight } from "@fortawesome/free-solid-svg-icons"
import { motion } from "framer-motion"
import Card from "../atoms/Card"
import { theme } from "../settings/config"
import { modalContext } from "../providers/modal.provider"
import { lessonsContext } from "../providers/lessons.provider"
import { profileContext } from "../providers/profile.provider"
import { schoolContext } from "../providers/school.provider"
import { playersContext } from "../providers/players.provider"
import { countSchoolLessons } from "../utils/firestore.utils"
import { teachesInSchool } from "../utils/func.utils"
import { ownershipStepOf, ownsSchool } from "../utils/school.utils"
import { callFunction } from "../services/firebase.service"
import { toast } from "react-toastify"
import DashedOutline from "../atoms/DashedOutline"

// I primi passi di chi è appena arrivato.
//
// Non è un tour: un tour racconta, e su un'app vuota non c'è niente da
// raccontare. Qui ogni riga è una cosa da fare, porta dove si fa, e sparisce
// quando è fatta — e "è fatta" lo dicono gli stessi dati che legge l'app,
// non una spunta salvata da qualche parte. Così la scheda non può mentire:
// non chiede un allievo a chi ne ha trenta, e non resta appesa per sempre
// come un cartello dimenticato.
function FirstStepsCard() {
	const Modal = useContext(modalContext)
	const { coach } = useContext(profileContext)
	const { selectedDate, lessons, weekLessons } = useContext(lessonsContext)
	const { selectedSchool, getSelectedSchool, getSchoolSettings } = useContext(schoolContext)
	const { activePlayers, loadedSchool } = useContext(playersContext)

	const [lessonsCount, setLessonsCount] = useState(null)
	const [isAnswering, setIsAnswering] = useState(false)

	useEffect(() => {
		let isCurrent = true
		if (!selectedSchool) return

		countSchoolLessons(selectedSchool)
			.then(count => isCurrent && setLessonsCount(count))
			.catch(err => {
				// se non riusciamo a contare, meglio tacere che mostrare un
				// passo già fatto come se fosse da fare
				console.error("Non riesco a contare le lezioni:", err)
				if (isCurrent) setLessonsCount(1)
			})

		return () => {
			isCurrent = false
		}
	}, [selectedSchool])

	// Finché non sappiamo, non si dice niente: una scheda che appare e
	// sparisce mentre l'utente la legge è peggio che non averla.
	// Anche chi c'è nel circolo: dice se la direzione è già entrata, e se
	// c'è già un maestro oltre a chi dirige.
	const school = getSelectedSchool()
	const isReady = Boolean(coach) && Boolean(school) &&
		loadedSchool === selectedSchool && lessonsCount !== null &&
		!Array.isArray(school.coaches)
	if (!isReady) return null

	const others = Object.values(school.coaches ?? {}).filter(
		other => other.coachID !== coach.coachID,
	)

	const hasPrices = Object.keys(getSchoolSettings()?.lessonPrices ?? {}).length > 0

	// Il conteggio è una fotografia scattata all'apertura: la lezione segnata
	// un attimo fa non ci sarebbe. Gli altri passi vengono da listener vivi e
	// si barrano da soli, e anche questo deve farlo — quindi vale pure quello
	// che l'app ha già in mano per i giorni che sta guardando (sul telefono,
	// la settimana).
	const hasLessonsInMemory =
		Object.values(lessons?.[selectedSchool] ?? {}).some(byDate =>
			Object.values(byDate ?? {}).some(list => (list ?? []).length > 0),
		) || Object.values(weekLessons ?? {}).some(list => (list ?? []).length > 0)

	const steps = [
		{
			id: "players",
			text: "Aggiungi il primo allievo",
			hint: "uno basta per cominciare, gli altri si mettono strada facendo",
			done: activePlayers.length > 0,
			action: () => Modal.open("newPlayer"),
		},
		{
			id: "lesson",
			text: "Segna la prima lezione",
			hint: "da qui nascono presenze, incassi e ore di campo",
			done: lessonsCount > 0 || hasLessonsInMemory,
			action: () => Modal.open("newLesson", selectedDate),
		},
		{
			id: "prices",
			text: "Metti i prezzi delle tue lezioni",
			hint: "senza, l'app non può calcolare quanto hai incassato",
			done: hasPrices,
			action: () => Modal.open("schoolSection", "prezzi"),
		},
	]

	// Il calendario è un di più, non un passo mancante: si mostra finché la
	// scheda c'è, ma non è lui a tenerla in piedi.
	const optional = {
		id: "calendar",
		text: "Collega Google Calendar",
		hint: "facoltativo: le lezioni compaiono anche sul tuo calendario",
		done: coach?.googleCalendar?.connected === true,
		action: () => Modal.open("googleCalendar"),
	}

	// Chi qui non insegna — un direttore, la segreteria — lezioni e prezzi
	// suoi non ne ha, e niente da mettere sul calendario: gli resta da
	// cominciare con gli allievi. Se il circolo è suo, da far entrare i
	// maestri: gli allievi li segnano loro.
	const teaches = teachesInSchool(coach, selectedSchool)
	const isOwner = ownsSchool(coach, school)
	const directorSteps = [
		{ id: "school", text: "Crea il circolo", done: true },
		{
			id: "coach",
			text: "Fai entrare il primo maestro",
			hint: "con un codice: dopo, vedi la sua giornata e le sue lezioni",
			done: others.some(other => teachesInSchool(other, selectedSchool)),
			action: () => Modal.open("schoolInvite"),
			accent: true,
		},
	]
	const ownSteps = teaches
		? steps
		: isOwner
			? directorSteps
			: steps.filter(step => step.id === "players")

	// Di chi è il circolo. Chi l'ha creato ne è il proprietario nell'app, ma
	// non sempre lo dirige: se è un maestro, il passo è proporre l'app a chi
	// lo dirige, e finisce quando la direzione è entrata da gestore. Chi
	// l'ha creato prima che lo si chiedesse non l'ha detto: glielo si chiede
	// qui, con due bottoni, e il "sì" chiude il discorso.
	const ownershipStep = ownershipStepOf(coach, school, selectedSchool)
	const ownership =
		ownershipStep === "ask"
			? { id: "ask", done: false }
			: ownershipStep
				? {
						id: "propose",
						text: "Proponi l'app a chi gestisce il circolo",
						hint: "la direzione o la segreteria: entrano con un codice",
						done: ownershipStep === "proposed",
						action: () => Modal.open("proposeApp"),
						accent: true,
					}
				: null

	const ownRows = ownership ? [...ownSteps, ownership] : ownSteps
	const missing = ownRows.filter(step => !step.done)
	if (missing.length === 0) return null

	const rows = teaches ? [...ownRows, optional] : ownRows

	const answer = async runByOwner => {
		setIsAnswering(true)
		try {
			const setRunByOwner = callFunction("setSchoolRunByOwner")
			await setRunByOwner({ schoolID: selectedSchool, runByOwner })
		} catch (err) {
			toast.error(err.message || "Non sono riuscito a salvarlo", { position: "top-center" })
		} finally {
			setIsAnswering(false)
		}
	}

	const answerButton = (text, runByOwner, bg, color) => (
		<motion.button
			type="button"
			whileTap={{ scale: 0.97 }}
			disabled={isAnswering}
			onClick={() => answer(runByOwner)}
			style={{
				flex: 1,
				height: "2.5rem",
				border: 0,
				borderRadius: "14px",
				background: bg,
				color,
				font: "inherit",
				fontWeight: 600,
				cursor: "pointer",
				opacity: isAnswering ? 0.6 : 1,
			}}>
			{text}
		</motion.button>
	)

	const askRow = (
		<Div key="ask" p={{ y: "0.7rem" }}>
			<Div d="flex" align="center" style={{ gap: "0.75rem" }}>
				<Div w="22px" h="22px" pos="relative" style={{ flexShrink: 0 }}>
					<DashedOutline color={theme.colors.primary} width={2} radius={11} dash="4 4" />
				</Div>
				<Div flexGrow="1">
					<Text textSize="body">{`${school.schoolName ?? "Il circolo"} è tuo?`}</Text>
					<Text textSize="caption" textColor={theme.colors.text_light}>
						se lo dirige qualcun altro, puoi proporgli l'app
					</Text>
				</Div>
			</Div>
			{/* i bottoni partono dal testo, non dal tondo */}
			<Div d="flex" m={{ t: "0.75rem", l: "calc(22px + 0.75rem)" }} style={{ gap: "0.6rem" }}>
				{answerButton("Sì, è mio", true, theme.colors.background, theme.colors.gunmetal)}
				{answerButton("No, ci insegno", false, theme.colors.primary_light, theme.colors.primary)}
			</Div>
		</Div>
	)

	return (
		<Card bg={theme.colors.surface}>
			<Text textWeight="bold" textSize="subheader" m={{ b: "0.25rem" }}>
				Primi passi
			</Text>
			<Text textSize="caption" textColor={theme.colors.text_light} m={{ b: "1rem" }}>
				{missing.length === 1
					? "Ne manca uno e hai finito."
					: `Ne mancano ${missing.length} e questa scheda sparisce.`}
			</Text>

			{rows.map(step => step.id === "ask" ? askRow : (
				<motion.div key={step.id} whileTap={{ scale: step.done ? 1 : 0.99 }}>
					<Div
						d="flex"
						align="center"
						p={{ y: "0.7rem" }}
						border="bottom"
						borderColor={theme.colors.gray_light}
						cursor={step.done ? "default" : "pointer"}
						style={{ gap: "0.75rem" }}
						onClick={() => !step.done && step.action()}>
						<Div
							w="22px"
							h="22px"
							rounded="circle"
							d="flex"
							align="center"
							justify="center"
							pos="relative"
							style={{ flexShrink: 0 }}
							bg={step.done ? theme.colors.primary : "transparent"}>
							{step.done ? (
								<FontAwesomeIcon icon={faCheck} color="white" size="xs" />
							) : (
								// su un tondo così piccolo i trattini sono più corti
								<DashedOutline
									color={step.accent ? theme.colors.primary : theme.colors.gray_light}
									width={2}
									radius={11}
									dash="4 4"
								/>
							)}
						</Div>

						<Div flexGrow="1">
							<Text
								textSize="body"
								textColor={step.done ? theme.colors.text_light : theme.colors.text}
								style={{
									textDecoration: step.done ? "line-through" : "none",
								}}>
								{step.text}
							</Text>
							{!step.done && (
								<Text textSize="caption" textColor={theme.colors.text_light}>
									{step.hint}
								</Text>
							)}
						</Div>

						{!step.done && (
							<FontAwesomeIcon
								icon={faChevronRight}
								color={theme.colors.text_light}
								size="sm"
							/>
						)}
					</Div>
				</motion.div>
			))}
		</Card>
	)
}

export default FirstStepsCard
